import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, CheckCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react'
import { useInvestorDeviceStats } from '@/hooks/useInvestorDeviceStats'

export function InvestorDeviceStatsCard() {
  const { stats, isConnected, isConnecting, error, reconnect } = useInvestorDeviceStats()

  // 连接状态指示器
  const ConnectionStatusBadge = () => {
    if (isConnecting) {
      return (
        <Badge variant="secondary" className="gap-1">
          <RefreshCw className="h-3 w-3 animate-spin" />
          連接中
        </Badge>
      )
    }

    if (isConnected) {
      return (
        <Badge variant="default" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          已連接
        </Badge>
      )
    }

    return (
      <Badge variant="destructive" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        未連接
      </Badge>
    )
  }

  // 格式化时间戳
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg font-medium">設備連接狀態</CardTitle>
        <div className="flex items-center gap-2">
          <ConnectionStatusBadge />
          {error && (
            <Button variant="outline" size="sm" onClick={reconnect} className="gap-1">
              <RefreshCw className="h-3 w-3" />
              重新連接
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* 错误状态 */}
        {error && (
          <div className="text-center py-4">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
            <p className="text-sm text-destructive mb-2">{error}</p>
            <Button variant="outline" size="sm" onClick={reconnect}>
              重新連接
            </Button>
          </div>
        )}

        {/* 加载状态 */}
        {isConnecting && !stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-12" />
              </div>
            ))}
          </div>
        )}

        {/* 统计数据 */}
        {stats && (
          <div className="space-y-4">
            {/* 主要统计 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Wifi className="h-4 w-4 text-green-600" />
                  <span className="text-xs text-muted-foreground">連線裝置</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {stats.totalConnectedDevices}
                </div>
              </div>

              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <WifiOff className="h-4 w-4 text-red-600" />
                  <span className="text-xs text-muted-foreground">未連線裝置</span>
                </div>
                <div className="text-2xl font-bold text-red-600">
                  {stats.totalDisconnectedDevices}
                </div>
              </div>

              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <CheckCircle className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-muted-foreground">連線診所</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {stats.connectedClinicsCount}
                </div>
              </div>

              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className="text-xs text-muted-foreground">總診所數</span>
                </div>
                <div className="text-2xl font-bold">{stats.totalClinicsCount}</div>
              </div>
            </div>

            {/* 连接率 */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                設備連線率:{' '}
                <span className="font-medium">
                  {stats.totalConnectedDevices + stats.totalDisconnectedDevices > 0
                    ? Math.round(
                        (stats.totalConnectedDevices /
                          (stats.totalConnectedDevices + stats.totalDisconnectedDevices)) *
                          100,
                      )
                    : 0}
                  %
                </span>
              </span>
              <span className="text-muted-foreground">
                診所連線率:{' '}
                <span className="font-medium">
                  {stats.totalClinicsCount > 0
                    ? Math.round((stats.connectedClinicsCount / stats.totalClinicsCount) * 100)
                    : 0}
                  %
                </span>
              </span>
            </div>

            {/* 详细诊所统计 */}
            {/* {stats.clinicStats && stats.clinicStats.length > 0 && (
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium mb-2">診所詳情</h4>
                <div className="grid gap-2 max-h-32 overflow-y-auto">
                  {stats.clinicStats.map((clinic) => (
                    <div key={clinic.clinicId} className="flex items-center justify-between text-xs p-2 bg-muted/30 rounded">
                      <span>診所 {clinic.clinicId}</span>
                      <div className="flex items-center gap-2">
                        <span className={clinic.hasOnlineDevices ? 'text-green-600' : 'text-red-600'}>
                          {clinic.connectedDevices}/{clinic.totalDevices}
                        </span>
                        <Badge variant={clinic.hasOnlineDevices ? 'default' : 'secondary'} className="text-xs">
                          {clinic.hasOnlineDevices ? '在線' : '離線'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* 更新时间 */}
            <div className="text-xs text-muted-foreground text-center border-t pt-2">
              最後更新: {formatTimestamp(stats.timestamp)}
            </div>
          </div>
        )}

        {/* 无数据状态 */}
        {!stats && !isConnecting && !error && (
          <div className="text-center py-4 text-muted-foreground">
            <WifiOff className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">暫無設備狀態數據</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
